import { create } from '@thoughtspot/logger';
import {
    MetricsService,
    PropertyMap,
    UserAction,
    userActionTypeEnum,
} from '@thoughtspot/metrics';
import type { SpreadsheetTelemetryEvent } from '@thoughtspot/spreadsheet';
import type { SpreadsheetAnswerContext } from './spreadsheet-answer-context';

const Logger = create('spreadsheet-mixpanel-util');

const trackSpreadsheetEvent = (
    action: typeof userActionTypeEnum[keyof typeof userActionTypeEnum],
    data: PropertyMap,
    metricsService: MetricsService,
) => {
    try {
        metricsService.collectEvent(new UserAction(action), data);
    } catch (error) {
        Logger.error(error, `Failed to track ${action} event`);
    }
};

export type SpreadsheetEntryPoint =
    | 'home_nav' // keeping it as per prd for now, will be a follow up task to look into in phase 2
    | 'search_data'
    | 'answer_reopen';

// Extract all the functions from the tokens
export const formulaFunctionNames = (
    tokens: ReadonlyArray<{ type?: string | null; text?: string | null }>,
): string[] => [
    ...new Set(
        tokens
            .filter(token => token.type === 'FUNCTION_NAME')
            .map(token => token.text?.toLowerCase())
            .filter((text): text is string => !!text),
    ),
];

// A save transition flips the session to saved without the user having opened
// anything — either an in-place isSaved flip or the first-save route remount on
// the flag-off path. Either would otherwise read as a fresh open.
let suppressNextOpenedEvent = false;

/** Call immediately before a save transition, so the open it triggers is not
 *  reported as a user-initiated open. */
export const suppressNextSpreadsheetOpenedEvent = (): void => {
    suppressNextOpenedEvent = true;
};

/** True once per suppression, then rearms. Callers report the open when this
 *  returns false. */
export const consumeSpreadsheetOpenedSuppression = (): boolean => {
    if (!suppressNextOpenedEvent) {
        return false;
    }
    suppressNextOpenedEvent = false;
    return true;
};

export const trackSpreadsheetOpened = (
    data: {
        entryPoint: SpreadsheetEntryPoint;
        answerContext: SpreadsheetAnswerContext;
    },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_OPENED,
        {
            entry_point: data.entryPoint,
            answer_id: data.answerContext.answerId,
            model_guid: data.answerContext.modelGuid,
        },
        metricsService,
    );

// Tracks the Query/Spreadsheet toggle click on the answer screen — a UI
// control interaction, distinct from spreadsheet-opened (arrival in
// spreadsheet mode by any means, e.g. redirects or direct URLs).
export const trackSpreadsheetToggleClick = (metricsService: MetricsService) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_TOGGLE_CLICK,
        {},
        metricsService,
    );

export const trackSpreadsheetFormulaCreated = (
    data: {
        /** The resolved column type — MEASURE / ATTRIBUTE / UNKNOWN. */
        formulaType: string;
        columnCount: number;
        formulaFunctions: string[];
        columnIndex: number;
    },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_FORMULA_CREATED,
        {
            formula_type: data.formulaType,
            column_count: data.columnCount,
            formula_functions: data.formulaFunctions,
            column_index: data.columnIndex,
        },
        metricsService,
    );

export const trackSpreadsheetSaved = (
    data: {
        columnCount: number;
        formulaCount: number;
        saveType: 'new' | 'overwrite';
        hasSort: boolean;
        answerContext: SpreadsheetAnswerContext;
    },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_SAVED,
        {
            column_count: data.columnCount,
            formula_count: data.formulaCount,
            save_type: data.saveType,
            has_sort: data.hasSort,
            has_filter: data.answerContext.hasFilter,
            has_conditional_format: data.answerContext.hasConditionalFormat,
        },
        metricsService,
    );

export const trackSpreadsheetExported = (
    data: { exportFormat: 'CSV' | 'XLSX'; rowCount: number },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_EXPORTED,
        {
            export_format: data.exportFormat,
            row_count: data.rowCount,
        },
        metricsService,
    );

export const trackSpreadsheetColumnAdded = (
    data: { columnType: 'formula' | 'data'; columnIndex: number },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_COLUMN_ADDED,
        {
            column_type: data.columnType,
            column_index: data.columnIndex,
        },
        metricsService,
    );

export const trackSpreadsheetColumnDeleted = (
    data: { hadFormula: boolean },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_COLUMN_DELETED,
        { had_formula: data.hadFormula },
        metricsService,
    );

export const trackSpreadsheetFormulaEdited = (
    data: { formulaFunctions: string[] },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_FORMULA_EDITED,
        { formula_functions: data.formulaFunctions },
        metricsService,
    );

/**
 * Routes an event raised inside the spreadsheet package onto Mixpanel.
 */
export const trackSpreadsheetPackageEvent = (
    event: SpreadsheetTelemetryEvent,
    metricsService: MetricsService,
) => {
    if (event.name === 'sort-applied') {
        trackSpreadsheetEvent(
            userActionTypeEnum.SPREADSHEET_SORT_APPLIED,
            event.props,
            metricsService,
        );
    }
};

export const trackSpreadsheetFullscreenChange = (
    data: { isFullscreen: boolean },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        data.isFullscreen
            ? userActionTypeEnum.SPREADSHEET_FULL_SCREEN_ENTERED
            : userActionTypeEnum.SPREADSHEET_FULL_SCREEN_EXITED,
        {},
        metricsService,
    );
